import { TInvoiceActivityGroup, TInvoiceActivityRow } from "./types";
import { dollarFormatter } from "utils";
import i18n from "i18n";

export const INVOICE_ACTIVITY_LABELS: Record<string, string> = {
  "invoice-send": "Invoice sent",
  "invoice-mark-sent": "Invoice marked sent",
  "invoice-resend": "Invoice resent",
  "invoice-status-change": "Status changed",
  "invoice-template-change": "Template changed",
  "invoice-completion-notes-change": "Completion notes changed",
  "invoice-billing-address-change": "Billing address changed",
  "invoice-files-change": "Files changed",
  "invoice-send-flag-change": "Sent flag changed",
  "invoice-due-date-change": "Due date changed",
  "invoice-creation-date-change": "Creation date changed",
  "invoice-create": "Invoice created",
  "invoice-line-add": "Line item added",
  "invoice-line-update": "Line item updated",
  "invoice-line-remove": "Line item removed",
  "invoice-line-rate-update": "Line item rate updated",
  "invoice-line-label-update": "Line item label updated",
  "invoice-line-quantity-update": "Line item quantity updated",
  "invoice-line-description-update": "Line item description updated",
  "invoice-line-tax-update": "Line item tax updated",
  "invoice-tax-add": "Tax added",
  "invoice-tax-update": "Tax updated",
  "invoice-tax-remove": "Tax removed",
  "invoice-fee-add": "Fee added",
  "invoice-fee-remove": "Fee removed",
  "invoice-fee-delete": "Fee deleted",
  "invoice-discount-add": "Discount added",
  "invoice-discount-remove": "Discount removed",
  "invoice-discount-delete": "Discount deleted",
  // common past-tense variants some backends emit
  "invoice-fee-added": "Fee added",
  "invoice-fee-removed": "Fee removed",
  "invoice-fee-deleted": "Fee deleted",
  "invoice-discount-added": "Discount added",
  "invoice-discount-removed": "Discount removed",
  "invoice-discount-deleted": "Discount deleted",
  "invoice-payment-refund": "Payment refund",
  "invoice-payment-refund-partial": "Partial payment refund",
  "invoice-payment-refund-full": "Full payment refund",
  "payment-refund": "Payment refund",
};

/** Title-case unknown `invoice-*` types instead of showing raw keys */
export function humanizeInvoiceActivityType(type: string) {
  return type
    .replace(/^invoice-/i, "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function getInvoiceActivityLabel(type?: string | null) {
  if (!type) return "Activity";
  return INVOICE_ACTIVITY_LABELS[type] || humanizeInvoiceActivityType(type);
}

/**
 * When several activity rows share the same timestamp, higher priority appears first
 * (timeline is sorted newest-first). Rows that share a timestamp are ordered so the
 * **capstone** events appear first: paid off, then status change, then payment apply/refund,
 * which matches the usual dependency chain when read top-to-bottom.
 */
export function invoiceActivityTimelinePriority(type?: string | null): number {
  const t = (type || "").toLowerCase().replace(/_/g, "-");
  if (t === "invoice-paid-off" || /invoice[_-]paid[_-]off/i.test(t)) return 30;
  if (/^invoice[_-](send|resend|mark-sent)$/.test(t)) return 25;
  if (t === "invoice-status-change") return 20;
  if (/invoice[_-]payment[_-]refund|payment[_-]refund|^invoice[_-]refund/i.test(t)) return 15;
  if (
    /^invoice-payment-(apply|applied|unapply|unapplied|reallocate|reallocated)$/.test(t) ||
    /^payment-(apply|applied|unapply|unapplied)$/.test(t)
  ) {
    return 10;
  }
  return 0;
}

function numericTimelineRowId(id: string | number | undefined): number {
  if (id == null) return 0;
  if (typeof id === "number" && Number.isFinite(id)) return id;
  if (typeof id === "string" && /^\d+$/.test(id.trim())) return parseInt(id.trim(), 10);
  return 0;
}

/** Newest timestamp first; equal timestamps use pipeline priority, then higher row id. */
export function compareInvoiceActivityTimelineRows(a: TInvoiceActivityRow, b: TInvoiceActivityRow): number {
  const at = new Date(a.timestamp).getTime();
  const bt = new Date(b.timestamp).getTime();
  const aBad = Number.isNaN(at);
  const bBad = Number.isNaN(bt);
  if (aBad !== bBad) return aBad ? 1 : -1;
  if (bt !== at) return bt - at;
  const pa = invoiceActivityTimelinePriority(a.type);
  const pb = invoiceActivityTimelinePriority(b.type);
  if (pb !== pa) return pb - pa;
  return numericTimelineRowId(b.id) - numericTimelineRowId(a.id);
}

function safeParseJson(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return { ok: false as const, value: trimmed };
  if (trimmed[0] !== "{" && trimmed[0] !== "[") return { ok: false as const, value: value };
  try {
    return { ok: true as const, value: JSON.parse(trimmed) };
  } catch {
    return { ok: false as const, value: value };
  }
}

export type TDiffEntry = {
  path: string;
  previous: unknown;
  next: unknown;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function diffObjects(prev: unknown, next: unknown, basePath = ""): TDiffEntry[] {
  if (!isPlainObject(prev) || !isPlainObject(next)) {
    if (prev !== next) {
      return [{ path: basePath || "value", previous: prev, next }];
    }
    return [];
  }

  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const diffs: TDiffEntry[] = [];
  for (const key of keys) {
    const p = (prev as Record<string, unknown>)[key];
    const n = (next as Record<string, unknown>)[key];
    const path = basePath ? `${basePath}.${key}` : key;

    if (isPlainObject(p) && isPlainObject(n)) {
      diffs.push(...diffObjects(p, n, path));
      continue;
    }
    if (Array.isArray(p) && Array.isArray(n)) {
      if (JSON.stringify(p) !== JSON.stringify(n)) diffs.push({ path, previous: p, next: n });
      continue;
    }
    if (p !== n) diffs.push({ path, previous: p, next: n });
  }
  return diffs;
}

export function buildDiff(previous_value?: string | null, new_value?: string | null): TDiffEntry[] | null {
  const prevRaw = previous_value ?? "";
  const nextRaw = new_value ?? "";
  if (!prevRaw && !nextRaw) return null;

  const prevParsed = safeParseJson(prevRaw);
  const nextParsed = safeParseJson(nextRaw);
  if (prevParsed.ok && nextParsed.ok) {
    const diffs = diffObjects(prevParsed.value, nextParsed.value);
    return diffs.length ? diffs : null;
  }

  if ((previous_value ?? "") !== (new_value ?? "")) {
    return [
      {
        path: "value",
        previous: previous_value ?? "",
        next: new_value ?? "",
      },
    ];
  }
  return null;
}

const DIFF_PATH_LABELS: Record<string, string> = {
  sale_unit_price: "Sale price",
  list_price: "List price",
  install_price: "Install price",
  price_type: "Price type",
  unit_price: "Unit price",
  rate: "Rate",
};

/** Display label for a diff field path (e.g. `install_price` → `Install price`). */
export function humanizeDiffPath(path: string): string {
  const leaf = path.split(".").pop() || path;
  if (DIFF_PATH_LABELS[leaf]) return DIFF_PATH_LABELS[leaf];
  return leaf
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatInvoicePriceTypeLabel(priceType?: string | null) {
  const t = (priceType || "").toLowerCase();
  if (t === "sale") return "Sale";
  if (t === "install") return "Install";
  if (t === "list") return "List";
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

type TLineItemRateSnap = {
  label?: string | null;
  quantity?: number | null;
  price_type?: string | null;
  rate?: number | null;
  sale_unit_price?: number | null;
  list_price?: number | null;
  install_price?: number | null;
  unit_price?: number | null;
};

function parseLineItemRateSnap(value: unknown): TLineItemRateSnap | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const parsed = parseMaybeJson(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as TLineItemRateSnap)
      : null;
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as TLineItemRateSnap;
  }
  return null;
}

/**
 * Readable line-item price changes from invoice-line-rate-update audit payloads.
 * Uses sale/list/install columns instead of the active "rate" (which shifts when price type changes).
 */
export function formatLineItemRateChangeDetailLines(
  previousValue?: string | null,
  newValue?: string | null
): string[] {
  const before = parseLineItemRateSnap(previousValue);
  const after = parseLineItemRateSnap(newValue);
  if (!before || !after) return [];

  const lines: string[] = [];
  const bPt = (before.price_type || "").toLowerCase();
  const aPt = (after.price_type || "").toLowerCase();

  if (bPt !== aPt) {
    lines.push(
      `Price type: ${formatInvoicePriceTypeLabel(before.price_type)} → ${formatInvoicePriceTypeLabel(after.price_type)}`
    );
  }

  const priceFields = [
    ["sale_unit_price", "Sale price"],
    ["list_price", "List price"],
    ["install_price", "Install price"],
  ] as const;

  let granularChanged = false;
  for (const [key, label] of priceFields) {
    const b = asNumber(before[key]);
    const a = asNumber(after[key]);
    if (b !== a) {
      granularChanged = true;
      const from = b != null ? dollarFormatter(b) ?? "—" : "—";
      const to = a != null ? dollarFormatter(a) ?? "—" : "—";
      lines.push(`${label}: ${from} → ${to}`);
    }
  }

  if (!granularChanged) {
    const bRate = asNumber(before.rate ?? before.unit_price);
    const aRate = asNumber(after.rate ?? after.unit_price);
    if (bRate !== aRate) {
      const label =
        aPt === "install" ? "Install price" : aPt === "list" ? "List price" : "Sale price";
      const from = bRate != null ? dollarFormatter(bRate) ?? "—" : "—";
      const to = aRate != null ? dollarFormatter(aRate) ?? "—" : "—";
      lines.push(`${label}: ${from} → ${to}`);
    }
  }

  const hasPriceTypeLine = lines.some((line) => line.startsWith("Price type:"));
  if (lines.length > 0 && !hasPriceTypeLine && aPt) {
    lines.unshift(`Price type: ${formatInvoicePriceTypeLabel(after.price_type)}`);
  }

  return lines;
}

function lineItemSnapLabel(snap: TLineItemRateSnap | null): string {
  return (snap?.label || "").trim();
}

function activePriceLabelForType(priceType?: string | null) {
  const pt = (priceType || "sale").toLowerCase();
  if (pt === "install") return "Install price";
  if (pt === "list") return "List price";
  return "Sale price";
}

function activePriceCentsFromSnap(snap: TLineItemRateSnap): number | null {
  const pt = (snap.price_type || "sale").toLowerCase();
  const fromRate = asNumber(snap.rate ?? snap.unit_price);
  if (fromRate != null) return fromRate;
  if (pt === "install") return asNumber(snap.install_price);
  if (pt === "list") return asNumber(snap.list_price);
  return asNumber(snap.sale_unit_price);
}

/** Detail lines for a single line-item snapshot (add / remove). */
function formatLineItemSnapshotDetailLines(snap: TLineItemRateSnap | null): string[] {
  if (!snap) return [];
  const lines: string[] = [];
  const qty = formatQty(snap.quantity);
  if (qty) lines.push(`Qty ${qty}`);

  const pt = (snap.price_type || "").toLowerCase();
  if (pt) lines.push(`Price type: ${formatInvoicePriceTypeLabel(snap.price_type)}`);

  const active = activePriceCentsFromSnap(snap);
  if (active != null) {
    lines.push(`${activePriceLabelForType(snap.price_type)}: ${dollarFormatter(active)}`);
  }

  return lines;
}

function isLineItemReplacePair(a: TInvoiceActivityRow, b: TInvoiceActivityRow): boolean {
  const types = new Set([a.type, b.type]);
  if (!types.has("invoice-line-remove") || !types.has("invoice-line-add")) return false;
  if ((a.fk_user_id ?? null) !== (b.fk_user_id ?? null)) return false;

  const at = new Date(a.timestamp).getTime();
  const bt = new Date(b.timestamp).getTime();
  if (Number.isNaN(at) || Number.isNaN(bt) || Math.abs(at - bt) > 2000) return false;

  const removeRow = a.type === "invoice-line-remove" ? a : b;
  const addRow = a.type === "invoice-line-add" ? a : b;
  const before = parseLineItemRateSnap(removeRow.previous_value);
  const after = parseLineItemRateSnap(addRow.new_value);
  if (!before || !after) return false;

  const beforeLabel = lineItemSnapLabel(before);
  const afterLabel = lineItemSnapLabel(after);
  return beforeLabel.length > 0 && beforeLabel === afterLabel;
}

/**
 * When the API re-inserts a line (remove + add at the same time), collapse into one
 * rate-update row with arrow notation instead of two separate events.
 */
export function mergeLineItemReplacePairs(rows: TInvoiceActivityRow[]): TInvoiceActivityRow[] {
  const skipIds = new Set<string | number>();
  const mergedAtId = new Map<string | number, TInvoiceActivityRow>();

  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = rows[i];
      const b = rows[j];
      if (skipIds.has(a.id) || skipIds.has(b.id)) continue;
      if (!isLineItemReplacePair(a, b)) continue;

      const removeRow = a.type === "invoice-line-remove" ? a : b;
      const addRow = a.type === "invoice-line-add" ? a : b;
      const label = lineItemSnapLabel(parseLineItemRateSnap(removeRow.previous_value));
      const emitFrom = rows.findIndex((r) => r.id === a.id || r.id === b.id);
      const anchor = emitFrom >= 0 ? rows[emitFrom] : a;

      skipIds.add(removeRow.id);
      skipIds.add(addRow.id);
      mergedAtId.set(anchor.id, {
        ...removeRow,
        id: `merged-${removeRow.id}-${addRow.id}`,
        type: "invoice-line-rate-update",
        description: label
          ? `Invoice line item updated: ${label}`
          : "Invoice line item updated",
        previous_value: removeRow.previous_value,
        new_value: addRow.new_value,
      });
    }
  }

  if (skipIds.size === 0) return rows;

  const result: TInvoiceActivityRow[] = [];
  for (const row of rows) {
    if (skipIds.has(row.id)) {
      const merged = mergedAtId.get(row.id);
      if (merged) result.push(merged);
      continue;
    }
    result.push(row);
  }
  return result;
}

function isMoneyDiffPath(path: string): boolean {
  const leaf = (path.split(".").pop() || path).toLowerCase();
  if (
    leaf === "quantity" ||
    leaf === "ordering" ||
    leaf.startsWith("fk_") ||
    leaf.endsWith("_id")
  ) {
    return false;
  }
  if (/(^|_)(price|amount|total|due|paid|refund)/.test(leaf)) return true;
  if (leaf === "rate" && !leaf.startsWith("ref_")) return true;
  return false;
}

/** Format a single diff value for timeline display (cents → currency when applicable). */
export function formatDiffDisplayValue(path: string, value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  const leaf = (path.split(".").pop() || path).toLowerCase();
  if (leaf === "price_type" && typeof value === "string") {
    return formatInvoicePriceTypeLabel(value);
  }

  if (isMoneyDiffPath(path)) {
    const n = asNumber(value);
    if (n != null) return dollarFormatter(n) ?? String(n);
  }

  if (typeof value === "number") return String(value);

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (isMoneyDiffPath(path)) {
      const n = asNumber(trimmed);
      if (n != null) return dollarFormatter(n) ?? trimmed;
    }
    return truncate(trimmed, 120).text;
  }

  try {
    return truncate(JSON.stringify(value), 160).text;
  } catch {
    return String(value);
  }
}

export function groupInvoiceActivityRows(
  rows: TInvoiceActivityRow[],
  opts?: { windowSeconds?: number }
): TInvoiceActivityGroup[] {
  const windowSeconds = opts?.windowSeconds ?? 2;
  const groups: TInvoiceActivityGroup[] = [];

  const sorted = [...rows].sort((a, b) => {
    const at = new Date(a.timestamp).getTime();
    const bt = new Date(b.timestamp).getTime();
    return bt - at;
  });

  for (const row of sorted) {
    const last = groups[groups.length - 1];
    const rowUser = row.fk_user_id ?? null;
    const rowTs = new Date(row.timestamp).getTime();

    if (last) {
      const lastTs = new Date(last.timestamp).getTime();
      const withinWindow = Math.abs(lastTs - rowTs) <= windowSeconds * 1000;
      const sameUser = (last.fk_user_id ?? null) === rowUser;
      if (withinWindow && sameUser) {
        last.rows.push(row);
        continue;
      }
    }

    const userLabel = row.user?.toString()?.trim() || "System";
    const groupKey = `${rowUser ?? "system"}:${Math.floor(rowTs / (windowSeconds * 1000))}`;
    groups.push({
      groupKey,
      timestamp: row.timestamp,
      fk_user_id: rowUser,
      userLabel,
      rows: [row],
    });
  }

  return groups;
}

export function formatTimestamp(timestamp: string, timeZone?: string) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

export function truncate(value: string, max = 180) {
  if (value.length <= max) return { text: value, truncated: false };
  return { text: `${value.slice(0, max)}…`, truncated: true };
}

function collectEmailsFromValue(value: unknown, out: Set<string>, depth = 0) {
  if (value == null || depth > 10) return;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t.includes("@")) return;
    for (const part of t.split(/[,;\n\r]+/)) {
      const p = part.trim();
      if (p.includes("@")) out.add(p);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const x of value) collectEmailsFromValue(x, out, depth + 1);
    return;
  }
  if (isPlainObject(value)) {
    const o = value as Record<string, unknown>;
    const keys = [
      "to",
      "cc",
      "bcc",
      "recipients",
      "emails",
      "email",
      "to_email",
      "toEmail",
      "customer_email",
      "customerEmail",
      "send_to",
      "sendTo",
      "recipient_email",
      "recipientEmail",
      "mail_to",
      "mailTo",
      "primary_recipient",
      "primaryRecipient",
    ];
    for (const k of keys) collectEmailsFromValue(o[k], out, depth + 1);
    // Nested shapes (e.g. { send: { to: "…" } }) when backend does not use top-level keys
    for (const v of Object.values(o)) {
      if (v != null && typeof v === "object") collectEmailsFromValue(v, out, depth + 1);
    }
  }
}

/** Pull recipient addresses from invoice send / resend audit payloads when present.
 * Recognizes common keys (to, cc, recipients, …) and recurses into nested objects (depth-limited).
 * Backend should store at least one recipient on the activity row JSON for reliable timeline display.
 */
export function extractEmailsFromSendPayload(payload: unknown): string[] {
  const out = new Set<string>();
  collectEmailsFromValue(payload, out);
  return [...out];
}

/** Field-level audit rows emitted during send; hidden when a send row exists at the same time. */
const INVOICE_SEND_PRELUDE_TYPES = new Set([
  "invoice-due-date-change",
  "invoice-send-flag-change",
  "invoice-files-change",
  "invoice-billing-address-change",
  "invoice-completion-notes-change",
  "invoice-template-change",
  "invoice-status-change",
]);

const SEND_BUNDLE_MS = 5000;

export function isInvoiceSendActivityRow(row: Pick<TInvoiceActivityRow, "type" | "description">): boolean {
  const type = row.type || "";
  const desc = row.description || "";
  if (type === "invoice-create" || /\binvoice\s+created\b/i.test(desc)) return false;
  return (
    /^invoice[_-](send|resend|mark-sent)$/i.test(type) ||
    /\bInvoice\s+\d+\s+sent\b/i.test(desc) ||
    /\bInvoice\s+\d+\s+resent\b/i.test(desc) ||
    /\bInvoice\s+\d+\s+marked\s+sent\b/i.test(desc) ||
    /\b(invoice\s+)?(sent|resent)\b.*\b(email|e-mail)\b/i.test(desc) ||
    /\b(email|e-mail)\b.*\b(sent|delivered)\b/i.test(desc)
  );
}

function rowTimestampMs(row: TInvoiceActivityRow): number {
  const t = new Date(row.timestamp).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Drop per-field send noise when a send / mark-sent / resend row shares the same timestamp window. */
export function filterBundledInvoiceSendFieldChanges(rows: TInvoiceActivityRow[]): TInvoiceActivityRow[] {
  const sendTimes: number[] = [];
  for (const row of rows) {
    if (isInvoiceSendActivityRow(row)) sendTimes.push(rowTimestampMs(row));
  }
  if (!sendTimes.length) return rows;

  return rows.filter((row) => {
    if (!INVOICE_SEND_PRELUDE_TYPES.has(row.type || "")) return true;
    const t = rowTimestampMs(row);
    return !sendTimes.some((st) => st > 0 && t > 0 && Math.abs(st - t) <= SEND_BUNDLE_MS);
  });
}

function timelineSentToDetail(emailsJoined: string) {
  const key = "views.invoice.timeline.sentTo";
  const translated = i18n.t(key, { emails: emailsJoined });
  return translated === key ? `Sent to: ${emailsJoined}` : translated;
}

function buildInvoiceSendDetailLines(payload: unknown): string[] {
  const emails = extractEmailsFromSendPayload(payload);
  if (!emails.length) return [];
  return [timelineSentToDetail(emails.join(", "))];
}

function parseMaybeJson(value?: string | null) {
  if (!value) return null;
  const parsed = safeParseJson(value);
  return parsed.ok ? parsed.value : null;
}

/** Known `fk_status_id` values used for company invoices (see invoices list tabs + invoice detail). */
const INVOICE_STATUS_I18N_KEY: Record<number, string> = {
  17: "views.invoices.index.Open",
  18: "views.invoices.index.Paid",
  33: "views.invoices.index.Unpaid",
  37: "views.invoice.index.Pending",
  38: "views.invoices.index.Partially Paid",
};

export function resolveInvoiceStatusIdLabel(id: number) {
  const key = INVOICE_STATUS_I18N_KEY[id];
  if (key) return i18n.t(key);
  return `#${id}`;
}

function asNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseStatusIdFromLogValue(v?: string | null): number | null {
  if (v == null) return null;
  const s = v.toString().trim();
  if (!s) return null;
  const obj = parseMaybeJson(s);
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const o = obj as Record<string, unknown>;
    const fromField = asNumber(o.fk_status_id ?? o.status_id);
    if (fromField != null) return fromField;
  }
  return asNumber(s);
}

function formatQty(v: unknown) {
  const n = asNumber(v);
  if (n == null) return null;
  // keep integers clean, avoid trailing .00 noise
  return Number.isInteger(n) ? String(n) : String(n);
}

function formatMoneyFromCents(v: unknown) {
  const n = asNumber(v);
  if (n == null) return null;
  // backend seems to store in cents for line items
  return dollarFormatter(n);
}

function firstArrayItem(v: unknown): unknown {
  return Array.isArray(v) ? v[0] : v;
}

function formatRateFromRef(obj: any): string | null {
  const rateRaw =
    obj?.ref_sale_rate ?? obj?.ref_install_rate ?? obj?.ref_list_rate ?? null;
  const rate = asNumber(rateRaw);
  if (rate == null) return null;

  const rateType = String(obj?.ref_list_type ?? obj?.ref_sale_type ?? obj?.ref_install_type ?? "")
    .toLowerCase()
    .trim();

  // Heuristic: if it's a percentage, convert basis points-ish numbers to % when obvious.
  if (rateType.includes("percent")) {
    // Backend examples observed: 5000.000, 3000.000, -10.000
    // Treat large magnitudes as basis points (5000 -> 50%).
    const abs = Math.abs(rate);
    const pct = abs >= 100 ? rate / 100 : rate;
    return `${pct}%`;
  }

  // Otherwise treat as cents if it's an integer-ish amount
  // (this matches other invoice monetary values usage)
  return dollarFormatter(rate);
}

function verbFromType(type: string): "Added" | "Updated" | "Deleted" {
  if (/(^|-)update(d)?$/.test(type)) return "Updated";
  if (/(^|-)(remove(d)?|delete(d)?)$/.test(type)) return "Deleted";
  return "Added";
}

function verbFromDescription(desc: string): "Added" | "Updated" | "Deleted" {
  const d = (desc || "").toLowerCase();
  if (d.includes("update")) return "Updated";
  if (d.includes("delete") || d.includes("removed")) return "Deleted";
  if (d.includes("add")) return "Added";
  return "Updated";
}

function categoryFromRefTaxType(refTaxType: string): "Fee" | "Discount" | "Tax" {
  const t = (refTaxType || "").toLowerCase();
  if (t === "fee") return "Fee";
  if (t === "discount") return "Discount";
  return "Tax";
}

function buildRefChangePrimary(params: {
  category: "Fee" | "Discount" | "Tax";
  verb: "Added" | "Updated" | "Deleted";
  refLabel: string;
}) {
  const { category, verb, refLabel } = params;
  const label = (refLabel || "").trim();
  return label ? `${category} ${verb}: ${label}` : `${category} ${verb}`;
}

function titleCaseWords(s: string) {
  return (s || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function deriveRefLabel(first: any, category: "Fee" | "Discount" | "Tax") {
  const raw = String(first?.ref_label || "").trim();
  if (raw) return titleCaseWords(raw);

  const listType = String(first?.ref_list_type ?? first?.ref_sale_type ?? first?.ref_install_type ?? "")
    .toLowerCase()
    .trim();
  if (listType.includes("percent")) return `${category} Percentage`;
  if (listType) return `${category} ${titleCaseWords(listType)}`;
  return category;
}

export type TInvoiceActivityFriendly = {
  primary?: string;
  detailLines?: string[];
  /** If true, suppress the generic diff rendering */
  hideDiff?: boolean;
};

/**
 * Make some high-volume audit events more readable than raw JSON diffs.
 * Falls back to `null` to allow generic rendering.
 */
export type TInvoiceCreationSource = "custom" | "appointment" | "estimate" | "unknown";

export function formatInvoiceCreatedPrimary(payload: {
  source?: string | null;
  parent_type?: string | null;
  mode?: string | null;
  price_type?: string | null;
  appointment_ids?: number[];
  estimate_option_ids?: number[];
} | null): string {
  const source = (payload?.source || "unknown").toLowerCase() as TInvoiceCreationSource;
  const parent =
    payload?.parent_type === "project"
      ? "project"
      : payload?.parent_type === "request"
        ? "request"
        : null;

  if (source === "appointment") {
    const ids = payload?.appointment_ids?.filter((n) => Number.isFinite(n)) ?? [];
    if (ids.length > 1) {
      return parent === "project"
        ? "Invoice created from appointments (project)"
        : parent === "request"
          ? "Invoice created from appointments (request)"
          : "Invoice created from appointments";
    }
    if (ids.length === 1) {
      return parent === "project"
        ? `Invoice created from appointment #${ids[0]} (project)`
        : parent === "request"
          ? `Invoice created from appointment #${ids[0]} (request)`
          : `Invoice created from appointment #${ids[0]}`;
    }
    if (payload?.mode === "all_by_price_type" && payload?.price_type) {
      return `Invoice created from appointments (${payload.price_type} price type)`;
    }
    return parent === "project"
      ? "Invoice created from appointment (project)"
      : parent === "request"
        ? "Invoice created from appointment (request)"
        : "Invoice created from appointment";
  }

  if (source === "estimate") {
    const ids = payload?.estimate_option_ids?.filter((n) => Number.isFinite(n)) ?? [];
    if (ids.length > 1) {
      return parent === "project"
        ? "Invoice created from estimates (project)"
        : parent === "request"
          ? "Invoice created from estimates (request)"
          : "Invoice created from estimates";
    }
    if (ids.length === 1) {
      return parent === "project"
        ? `Invoice created from estimate option #${ids[0]} (project)`
        : parent === "request"
          ? `Invoice created from estimate option #${ids[0]} (request)`
          : `Invoice created from estimate option #${ids[0]}`;
    }
    return parent === "project"
      ? "Invoice created from estimate (project)"
      : parent === "request"
        ? "Invoice created from estimate (request)"
        : "Invoice created from estimate";
  }

  if (source === "custom") {
    return parent === "project"
      ? "Invoice created from scratch (project)"
      : parent === "request"
        ? "Invoice created from scratch (request)"
        : "Invoice created from scratch";
  }

  return "Invoice created";
}

export function getInvoiceActivityFriendly(row: TInvoiceActivityRow): TInvoiceActivityFriendly | null {
  const type = row.type || "";

  const desc = row.description?.toString() || "";

  if (type === "invoice-line-rate-update") {
    const detailLines = formatLineItemRateChangeDetailLines(
      row.previous_value,
      row.new_value
    );
    const labelMatch = desc.match(/line item (?:rate )?updated:\s*(.+)$/i);
    const itemLabel = labelMatch?.[1]?.trim();
    return {
      primary: itemLabel
        ? `Line item updated: ${itemLabel}`
        : desc.trim() || getInvoiceActivityLabel(type),
      detailLines: detailLines.length ? detailLines : undefined,
      hideDiff: true,
    };
  }

  if (type === "invoice-line-add" || type === "invoice-line-added") {
    const snap = parseLineItemRateSnap(row.new_value);
    const label = lineItemSnapLabel(snap);
    const detailLines = formatLineItemSnapshotDetailLines(snap);
    return {
      primary: label ? `Line item added: ${label}` : getInvoiceActivityLabel(type),
      detailLines: detailLines.length ? detailLines : undefined,
      hideDiff: true,
    };
  }

  if (type === "invoice-line-remove" || type === "invoice-line-removed") {
    const snap = parseLineItemRateSnap(row.previous_value);
    const label = lineItemSnapLabel(snap);
    const detailLines = formatLineItemSnapshotDetailLines(snap);
    return {
      primary: label ? `Line item removed: ${label}` : getInvoiceActivityLabel(type),
      detailLines: detailLines.length ? detailLines : undefined,
      hideDiff: true,
    };
  }

  if (type === "invoice-create" || /\binvoice\s+created\b/i.test(desc)) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      return {
        primary: formatInvoiceCreatedPrimary(payload as Record<string, unknown>),
        hideDiff: true,
      };
    }
    if (/\bfrom\s+(appointment|estimate|scratch)\b/i.test(desc)) {
      return { primary: desc.trim(), hideDiff: true };
    }
    return {
      primary: desc.trim() || getInvoiceActivityLabel(type) || "Invoice created",
      hideDiff: true,
    };
  }

  const isPaidOff =
    type === "invoice-paid-off" ||
    /paid\s*off/i.test(type) ||
    /invoice\s+paid\s+off/i.test(desc);
  if (isPaidOff) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const p: any = payload;
      const amountDueBefore = asNumber(p.amount_due_before ?? p.amountDueBefore ?? p.amount_due);
      const amountDueAfter = asNumber(p.amount_due_after ?? p.amountDueAfter ?? p.amount_due_after_value);
      const paidTotalAfter = asNumber(p.paid_total_after ?? p.paidTotalAfter ?? p.paid_total_after_value);
      const paymentId = asNumber(p.paymentId ?? p.payment_id);

      const detailLines: string[] = [];
      if (amountDueBefore != null || amountDueAfter != null) {
        const to = amountDueAfter != null ? dollarFormatter(amountDueAfter) : dollarFormatter(0);
        // For paid-off, prefer the current (after) value only when it's available.
        // This avoids noisy "$0.00 → $0.00" lines.
        detailLines.push(`amount due: ${to}`);
      }
      if (paidTotalAfter != null) detailLines.push(`total paid: ${dollarFormatter(paidTotalAfter)}`);
      if (paymentId != null) detailLines.push(`payment ID: ${paymentId}`);

      return {
        primary: desc.trim() || "Invoice paid off",
        detailLines: detailLines.length ? detailLines : undefined,
        hideDiff: true,
      };
    }
  }

  const isPaymentActivity =
    /^invoice-payment-(apply|applied|unapply|unapplied|reallocate|reallocated)$/.test(type) ||
    /^payment-(apply|applied|unapply|unapplied)$/.test(type) ||
    /payment\s+(applied|unapplied|reallocated)/i.test(desc);
  if (isPaymentActivity) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const p: any = payload;

      const applied = asNumber(p.appliedAmount ?? p.applied_amount);
      const dueBefore = asNumber(p.invoiceAmountDueBefore ?? p.invoice_amount_due_before);
      const dueAfter = asNumber(p.invoiceAmountDueAfter ?? p.invoice_amount_due_after);
      const paidBefore = asNumber(p.invoicePaidTotalBefore ?? p.invoice_paid_total_before);
      const paidAfter = asNumber(p.invoicePaidTotalAfter ?? p.invoice_paid_total_after);
      const paymentId = asNumber(p.paymentId ?? p.payment_id);

      const detailLines: string[] = [];
      if (applied != null) detailLines.push(`applied amount: ${dollarFormatter(applied)}`);
      if (dueBefore != null || dueAfter != null) {
        const from = dueBefore != null ? dollarFormatter(dueBefore) : dollarFormatter(0);
        const to = dueAfter != null ? dollarFormatter(dueAfter) : dollarFormatter(0);
        detailLines.push(`amount due: ${from} → ${to}`);
      }
      if (paidBefore != null || paidAfter != null) {
        const from = paidBefore != null ? dollarFormatter(paidBefore) : dollarFormatter(0);
        const to = paidAfter != null ? dollarFormatter(paidAfter) : dollarFormatter(0);
        detailLines.push(`total paid: ${from} → ${to}`);
      }
      if (paymentId != null) detailLines.push(`payment ID: ${paymentId}`);

      return {
        primary: desc.trim() || "Payment updated",
        detailLines: detailLines.length ? detailLines : undefined,
        hideDiff: true,
      };
    }
  }

  const isPaymentRefund =
    /^invoice[_-]payment[_-]refund/i.test(type) ||
    /^invoice[_-]refund/i.test(type) ||
    /^payment[_-]refund/i.test(type) ||
    (/^payment[_-]/i.test(type) && /\brefund\b/i.test(type)) ||
    /\b(partial|full)\s+payment\s+refund\b/i.test(desc) ||
    /\bpayment\s+refund\b/i.test(desc);
  if (isPaymentRefund) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const p: any = payload;

      const refundAmt = asNumber(
        p.refundAmount ?? p.refund_amount ?? p.amountRefunded ?? p.amount_refunded ?? p.refundedAmount ?? p.refunded_amount
      );
      const dueBefore = asNumber(p.invoiceAmountDueBefore ?? p.invoice_amount_due_before);
      const dueAfter = asNumber(p.invoiceAmountDueAfter ?? p.invoice_amount_due_after);
      const paidBefore = asNumber(p.invoicePaidTotalBefore ?? p.invoice_paid_total_before);
      const paidAfter = asNumber(p.invoicePaidTotalAfter ?? p.invoice_paid_total_after);
      const paymentId = asNumber(p.paymentId ?? p.payment_id);

      const fullRaw = p.isFullRefund ?? p.full_refund ?? p.refund_type ?? p.refundType;
      let isFull: boolean | null = null;
      if (typeof fullRaw === "boolean") isFull = fullRaw;
      else if (typeof fullRaw === "string") {
        const s = fullRaw.toLowerCase().trim();
        if (s === "full" || s === "complete" || s === "total") isFull = true;
        else if (s === "partial") isFull = false;
      }

      const detailLines: string[] = [];
      if (refundAmt != null) detailLines.push(`refund amount: ${dollarFormatter(refundAmt)}`);
      if (dueBefore != null || dueAfter != null) {
        const from = dueBefore != null ? dollarFormatter(dueBefore) : dollarFormatter(0);
        const to = dueAfter != null ? dollarFormatter(dueAfter) : dollarFormatter(0);
        detailLines.push(`amount due: ${from} → ${to}`);
      }
      if (paidBefore != null || paidAfter != null) {
        const from = paidBefore != null ? dollarFormatter(paidBefore) : dollarFormatter(0);
        const to = paidAfter != null ? dollarFormatter(paidAfter) : dollarFormatter(0);
        detailLines.push(`total paid: ${from} → ${to}`);
      }
      if (paymentId != null) detailLines.push(`payment ID: ${paymentId}`);

      const primaryDefault =
        isFull === true
          ? "Full payment refund"
          : isFull === false
            ? "Partial payment refund"
            : getInvoiceActivityLabel(type) || "Payment refund";

      return {
        primary: desc.trim() || primaryDefault,
        detailLines: detailLines.length ? detailLines : undefined,
        hideDiff: true,
      };
    }
  }

  const isWriteOff =
    type === "invoice-writeoff" ||
    type === "invoice-write-off" ||
    /write[-\s]?off/i.test(type) ||
    /write[-\s]?off/i.test(desc);
  if (isWriteOff) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const p: any = payload;
      const amountCents = asNumber(p.writeOffAmount ?? p.write_off_amount ?? p.amount ?? p.amount_cents);
      const amount = amountCents != null ? dollarFormatter(amountCents) : null;
      const date = String(p.writeOffDate ?? p.write_off_date ?? p.date ?? "").trim();
      const reason = String(p.reason ?? "").trim();
      const syncedRaw = p.invoiceSynced ?? p.invoice_synced;
      const synced = typeof syncedRaw === "boolean" ? syncedRaw : null;

      const detailLines: string[] = [];
      if (amount) detailLines.push(`amount: ${amount}`);
      if (date) detailLines.push(`date: ${date}`);
      if (reason) detailLines.push(`reason: ${reason}`);
      if (synced != null) detailLines.push(`synced: ${synced}`);

      return {
        primary: desc.trim() || "Write-off recorded",
        detailLines: detailLines.length ? detailLines : undefined,
        hideDiff: true,
      };
    }
  }

  if (type === "invoice-status-change") {
    const prevId = parseStatusIdFromLogValue(row.previous_value);
    const nextId = parseStatusIdFromLogValue(row.new_value);
    if (prevId != null || nextId != null) {
      const prevLabel = prevId != null ? resolveInvoiceStatusIdLabel(prevId) : "—";
      const nextLabel = nextId != null ? resolveInvoiceStatusIdLabel(nextId) : "—";
      return {
        primary: desc.trim() || getInvoiceActivityLabel(type),
        detailLines: [`status: ${prevLabel} → ${nextLabel}`],
        hideDiff: true,
      };
    }
  }

  const isQuickBooksToggle =
    /qbsync|qb-sync|quickbooks.*sync/i.test(type) || /quickbooks\s+sync\s+toggled/i.test(desc);
  if (isQuickBooksToggle) {
    // Observed values: previous_value/new_value are strings like "disabled" or empty.
    // Treat "disabled" as OFF; null/empty as ON.
    const prev = (row.previous_value ?? "").toString().trim();
    const next = (row.new_value ?? "").toString().trim();
    const prevOff = prev.toLowerCase() === "disabled";
    const nextOff = next.toLowerCase() === "disabled";

    const prevEnabled = !prevOff;
    // If next is empty but prev was disabled => enabled; if next is disabled => disabled.
    const nextEnabled = next ? !nextOff : prevOff;

    return {
      primary: "QuickBooks sync changed",
      detailLines: [`enabled: ${prevEnabled} → ${nextEnabled}`],
      hideDiff: true,
    };
  }

  // Invoice send / resend / mark-sent — prefer recipient + customer from audit JSON (see docs).
  if (isInvoiceSendActivityRow(row)) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    const detailLines = buildInvoiceSendDetailLines(payload);
    const primary =
      getInvoiceActivityLabel(type) ||
      (/\bresent\b/i.test(desc) ? "Invoice resent" : /\bmarked\s+sent\b/i.test(desc) ? "Invoice marked sent" : "Invoice sent");
    return {
      primary: desc.trim() || primary,
      detailLines: detailLines.length ? detailLines : undefined,
      hideDiff: true,
    };
  }

  if (/^invoice-line-(update|updated|delete|deleted)$/.test(type)) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    const label = (payload && typeof payload === "object" && "label" in payload)
      ? String((payload as any).label || "").trim()
      : "";

    const qty = payload && typeof payload === "object" ? formatQty((payload as any).quantity) : null;
    const unit = payload && typeof payload === "object" ? formatMoneyFromCents((payload as any).unit_price) : null;

    const primaryBase = getInvoiceActivityLabel(type);
    const primary = label ? `${primaryBase}: ${label}` : primaryBase;

    const detailLines: string[] = [];
    if (qty) detailLines.push(`Qty ${qty}`);
    if (unit) detailLines.push(`Unit ${unit}`);

    return {
      primary,
      detailLines: detailLines.length ? detailLines : undefined,
      hideDiff: true,
    };
  }

  if (/^invoice-tax-(add|added|update|updated|remove|removed|delete|deleted)$/.test(type)) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    const first = firstArrayItem(payload);
    const refTaxType =
      first && typeof first === "object" && "ref_tax_type" in (first as any)
        ? String((first as any).ref_tax_type || "").trim().toLowerCase()
        : "";

    const category = categoryFromRefTaxType(refTaxType);
    const refLabel = deriveRefLabel(first, category);
    const verb = verbFromType(type);
    const primary = buildRefChangePrimary({ category, verb, refLabel });

    // only show helpful rate-ish fields if present (avoid giant null objects)
    const detailLines: string[] = [];
    if (first && typeof first === "object") {
      const formatted = formatRateFromRef(first);
      if (formatted) detailLines.push(`rate: ${formatted}`);
    }

    return {
      primary,
      detailLines: detailLines.length ? detailLines : undefined,
      hideDiff: true,
    };
  }

  // Some backends may emit explicit fee/discount types rather than tax types.
  const isFeeOrDiscountType =
    /^invoice-(fee|discount)-(add|added|update|updated|remove|removed|delete|deleted)$/.test(type);
  if (isFeeOrDiscountType) {
    const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
    const first = firstArrayItem(payload);
    const category = type.includes("discount") ? "Discount" : "Fee";
    const refLabel = deriveRefLabel(first, category);
    const verb = verbFromType(type);
    const primary = buildRefChangePrimary({ category, verb, refLabel });

    const detailLines: string[] = [];
    if (first && typeof first === "object") {
      const formatted = formatRateFromRef(first);
      if (formatted) detailLines.push(`rate: ${formatted}`);
    }
    return { primary, detailLines: detailLines.length ? detailLines : undefined, hideDiff: true };
  }

  // Fallback: if backend type doesn't match expected patterns, detect by payload shape.
  // This is what keeps UI stable when audit `type` strings drift.
  const payload = parseMaybeJson(row.new_value) ?? parseMaybeJson(row.previous_value);
  const first = firstArrayItem(payload);
  if (first && typeof first === "object") {
    const refTaxType = String((first as any).ref_tax_type || "").trim().toLowerCase();
    const hasRef = "ref_label" in (first as any) || "ref_list_rate" in (first as any);
    if (hasRef && (refTaxType === "fee" || refTaxType === "discount" || refTaxType === "tax")) {
      const category = categoryFromRefTaxType(refTaxType || "tax");
      const refLabel = deriveRefLabel(first, category);
      const verb = verbFromDescription(desc);
      const primary = buildRefChangePrimary({ category, verb, refLabel });

      const detailLines: string[] = [];
      const formatted = formatRateFromRef(first);
      if (formatted) detailLines.push(`rate: ${formatted}`);

      return { primary, detailLines: detailLines.length ? detailLines : undefined, hideDiff: true };
    }
  }

  return null;
}

